import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import moment from 'moment';
import Constants from 'style/constants';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import MonitoringIcon from 'shared/components/monitoring/monitoring-icon';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import PageHeader from 'shared/components/page-header/page-header';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import { formatFromMilliseconds } from 'shared/utils/format-from-milliseconds';

import { useFaultsHistoryContextProvider } from 'features/faults-history/context/faults-history-provider';
import Fault from 'features/faults-history/domain/models/fault';
import { FaultCategory } from 'features/faults-history/domain/models/fault-category';
import {
  FaultsHistoryConnectorFaultType,
  FaultsHistoryDeviceFaultType,
  FaultsHistoryGatewayFaultType,
  FaultsHistorySystemFaultType,
} from 'features/faults-history/domain/models/fault-type';
import FaultsHistoryFilters from 'features/faults-history/views/components/faults-history-filters/faults-history-filters';
import FaultsHistoryResolvedStatus from 'features/faults-history/views/components/faults-history-resolved-status';
import styles from 'features/faults-history/views/faults-history.module.scss';

export const FaultsHistory = () => {
  const { t } = useTranslation('faultsHistory');

  const {
    readFaultsHistoryData,
    readFaultsHistoryIsLoading,
    currentPage,
    currentRowsPerPage,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
  } = useFaultsHistoryContextProvider();

  const columns: ColumnDefinition<Fault, NestedKeyof<Fault>>[] = [
    {
      key: 'category',
      label: t('faultsTable.category'),
      renderCustomContentProvider: (fault) => <div>{t(`faultCategory.${fault.category}`)}</div>,
      disableSort: true,
    },
    {
      key: 'faultType',
      label: t('faultsTable.faultType'),
      renderCustomContentProvider: (fault) => <div>{getIcons(fault)}</div>,
      disableSort: true,
    },
    {
      key: 'name',
      label: t('faultsTable.name'),
      renderCustomContentProvider: (fault) => <div>{fault.name}</div>,
      disableSort: true,
    },
    {
      key: 'type',
      label: t('faultsTable.type'),
      renderCustomContentProvider: (fault) => {
        let typeDisplay = fault.displayType;
        if (!typeDisplay) {
          if (fault.category === FaultCategory.Gateway) {
            typeDisplay = t(`gatewayType.${fault.type}`);
          } else if (fault.category === FaultCategory.Device) {
            typeDisplay = t(`deviceType.${fault.type}`);
          } else if (fault.category === FaultCategory.System) {
            typeDisplay = t(`systemType.${fault.type}`);
          } else if (fault.category === FaultCategory.Connector) {
            typeDisplay = t(`connectorType.${fault.type}`);
          } else {
            typeDisplay = fault.type;
          }
        }
        return <div>{typeDisplay}</div>;
      },
      disableSort: true,
    },
    {
      key: 'organisationUnitId',
      label: t('faultsTable.location'),
      renderCustomContentProvider: (item) => {
        if (!item || !item.breadCrumbs) {
          return <></>;
        }
        const breadCrumbsArray = item.breadCrumbs;
        return (
          <div>
            <BreadCrumb showEllipsis={breadCrumbsArray.length > 2} allElementNames={breadCrumbsArray}>
              {breadCrumbsArray.map((name, index) => {
                return (
                  <Typography key={index} variant="caption">
                    {name}
                  </Typography>
                );
              })}
            </BreadCrumb>
          </div>
        );
      },
      disableSort: true,
    },
    {
      key: 'createdOn',
      label: t('faultsTable.createdOn'),
      renderCustomContentProvider: (item) => <>{moment(item.createdOn).format('DD-MM-YYYY · HH:mm:ss').toString()}</>,
      disableSort: true,
    },
    {
      key: 'resolvedOn',
      label: t('faultsTable.resolved'),
      renderCustomContentProvider: (item) => <FaultsHistoryResolvedStatus resolvedOn={item.resolvedOn} />,
      disableSort: true,
    },
    {
      key: 'duration',
      label: t('faultsTable.duration'),
      disableSort: true,
      renderCustomContentProvider: (item) => <>{formatFromMilliseconds(moment.duration(item.duration).asMilliseconds())}</>,
    },
  ];

  const getIcons = (fault: Fault) => {
    if (fault.category === FaultCategory.System) {
      switch (fault.faultType) {
        case FaultsHistorySystemFaultType.SystemOffline:
          return (
            <div className={styles.faultTypeIconContainer}>
              <MonitoringIcon
                key={`monitoring-icon-${FaultsHistorySystemFaultType.SystemOffline}`}
                type={MonitoringType.SystemOffline}
                withTooltip={false}
                visible={true}
                size={16}
              />
              <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
                {t('faultType.SystemOffline')}
              </Typography>
            </div>
          );
        case FaultsHistorySystemFaultType.IPUnitConfigurationError:
        case FaultsHistorySystemFaultType.IPUnitHighFrequencyError:
        case FaultsHistorySystemFaultType.IPUnitLocalBusError:
        case FaultsHistorySystemFaultType.IPUnitWirelessG2Error:
        case FaultsHistorySystemFaultType.IPUnitWirelessG2InterferenceError:
          return (
            <>
              <div className={styles.faultTypeIconContainer}>
                <MonitoringIcon
                  key={`monitoring-icon-${FaultsHistorySystemFaultType.IPUnitConfigurationError}`}
                  type={MonitoringType.SystemWarning}
                  withTooltip={false}
                  visible={true}
                  size={16}
                />
                <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
                  {t('faultType.SystemMalfunction')}
                </Typography>
              </div>
              <div className={styles.faultMessageContainer}>
                <Typography variant="caption">{fault.message}</Typography>
              </div>
            </>
          );
        case FaultsHistorySystemFaultType.IS32PowerError:
        case FaultsHistorySystemFaultType.IS32EarthError:
        case FaultsHistorySystemFaultType.IS32BatteryError:
        case FaultsHistorySystemFaultType.IS32CentralError:
        case FaultsHistorySystemFaultType.IS32SystemError:
        case FaultsHistorySystemFaultType.IS32AllAddressesTaken:
          return (
            <>
              <div className={styles.faultTypeIconContainer}>
                <MonitoringIcon
                  key={`monitoring-icon-${FaultsHistorySystemFaultType.IS32SystemError}`}
                  type={MonitoringType.SystemMalfunction}
                  withTooltip={false}
                  visible={true}
                  size={16}
                />
                <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
                  {t('faultType.SystemMalfunction')}
                </Typography>
              </div>
              <div className={styles.faultMessageContainer}>
                <Typography variant="caption">{fault.message}</Typography>
              </div>
            </>
          );
      }
    }

    if (fault.category === FaultCategory.Gateway) {
      switch (fault.faultType) {
        case FaultsHistoryGatewayFaultType.ServiceOffline:
          return (
            <div className={styles.faultTypeIconContainer}>
              <MonitoringIcon
                key={`monitoring-icon-${FaultsHistoryGatewayFaultType.ServiceOffline}`}
                type={MonitoringType.GatewayServiceOffline}
                withTooltip={false}
                visible={true}
                size={16}
              />
              <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
                {t('faultType.ServiceOffline')}
              </Typography>
            </div>
          );
        case FaultsHistoryGatewayFaultType.GatewayOffline:
          return (
            <div className={styles.faultTypeIconContainer}>
              <MonitoringIcon
                key={`monitoring-icon-${FaultsHistoryGatewayFaultType.GatewayOffline}`}
                type={MonitoringType.GatewayOffline}
                withTooltip={false}
                visible={true}
                size={16}
              />
              <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
                {t('faultType.GatewayOffline')}
              </Typography>
            </div>
          );
        case FaultsHistoryGatewayFaultType.PassiveGatewayOffline:
          return (
            <div className={styles.faultTypeIconContainer}>
              <MonitoringIcon
                key="monitoring-icon-passive-gateway-offline"
                type={MonitoringType.PassiveGatewayOffline}
                withTooltip={false}
                visible={true}
                size={16}
              />
              <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
                {t('faultType.PassiveGatewayOffline')}
              </Typography>
            </div>
          );
      }
    }

    if (fault.category === FaultCategory.Device) {
      switch (fault.faultType) {
        case FaultsHistoryDeviceFaultType.Unknown:
          return (
            <div className={styles.faultTypeIconContainer}>
              <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
                {t('faultType.Unknown')}
              </Typography>
            </div>
          );
        case FaultsHistoryDeviceFaultType.DeviceOffline:
          return (
            <div className={styles.faultTypeIconContainer}>
              <MonitoringIcon
                key={`monitoring-icon-${FaultsHistoryDeviceFaultType.DeviceOffline}`}
                type={MonitoringType.Offline}
                withTooltip={false}
                visible={true}
                size={16}
              />
              <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
                {t('faultType.DeviceOffline')}
              </Typography>
            </div>
          );
        case FaultsHistoryDeviceFaultType.BatteryLow:
          return (
            <div className={styles.faultTypeIconContainer}>
              <MonitoringIcon
                key={`monitoring-icon-${FaultsHistoryDeviceFaultType.BatteryLow}`}
                type={MonitoringType.BatteryLow}
                withTooltip={false}
                visible={true}
                size={16}
              />
              <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
                {t('faultType.BatteryLow')}
              </Typography>
            </div>
          );
        case FaultsHistoryDeviceFaultType.BatteryCritical:
          return (
            <div className={styles.faultTypeIconContainer}>
              <MonitoringIcon
                key={`monitoring-icon-${FaultsHistoryDeviceFaultType.BatteryCritical}`}
                type={MonitoringType.BatteryCritical}
                withTooltip={false}
                visible={true}
                size={16}
              />
              <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
                {t('faultType.BatteryCritical')}
              </Typography>
            </div>
          );
        case FaultsHistoryDeviceFaultType.IS32Error:
        case FaultsHistoryDeviceFaultType.IS32LocalbusError:
        case FaultsHistoryDeviceFaultType.IS32UnitError:
          return (
            <>
              <div className={styles.faultTypeIconContainer}>
                <MonitoringIcon
                  key={`monitoring-icon-${FaultsHistoryDeviceFaultType.IS32Error}`}
                  type={MonitoringType.DeviceMalfunction}
                  withTooltip={false}
                  visible={true}
                  size={16}
                />
                <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
                  {t('faultType.DeviceMalfunction')}
                </Typography>
              </div>
              <div className={styles.faultMessageContainer}>
                <Typography variant="caption">{fault.message}</Typography>
              </div>
            </>
          );
      }
    }

    if (fault.category === FaultCategory.Connector && fault.faultType === FaultsHistoryConnectorFaultType.ConnectorOffline) {
      return (
        <div className={styles.faultTypeIconContainer}>
          <MonitoringIcon
            key={`monitoring-icon-${FaultsHistoryConnectorFaultType.ConnectorOffline}`}
            type={MonitoringType.ConnectorOffline}
            withTooltip={false}
            visible={true}
            size={16}
          />
          <Typography variant="overline" sx={{ color: Constants.Colors.onSurface }}>
            {t('faultType.ConnectorOffline')}
          </Typography>
        </div>
      );
    }
  };

  return (
    <>
      <div className={styles.faultsContainer}>
        <PageHeader
          title={t('pageHeaderTitle')}
          filterComponent={
            <div className={styles.faultsHistoryFilterComponentContainer}>
              <FaultsHistoryFilters />
            </div>
          }
        />
      </div>
      {readFaultsHistoryIsLoading ? (
        <LoadingIndicator />
      ) : (
        <Table
          data={
            readFaultsHistoryData?.faults?.map<RowItem<Fault>>((fault) => ({
              isHighlighted: !fault.resolvedOn,
              data: fault,
            })) ?? []
          }
          columns={columns}
          pagination={{
            count: readFaultsHistoryData?.totalCount ?? 0,
            onPageChanged: handleOnPageChanged,
            rowsPerPage: currentRowsPerPage,
            page: currentPage,
            onRowsPerPageChanged: handleOnRowsPerPageChanged,
          }}
          rowIdentifier={(item: Fault) => item.id}
        />
      )}
    </>
  );
};

export default FaultsHistory;
